import { EstimateBalance, EstimateBalanceWorkflowStatus } from '@mmx/shared'

/** Outstanding owed; clamped to zero so over-applied payments don't render as negative dollars. */
export function balanceOutstanding(balance: EstimateBalance): number {
  return Math.max(0, balance.patientResponsibility - balance.paidAmount + balance.refundedAmount)
}

/** The `invoices` URL segment is shared with legacy invoices; UnifiedEstimatorGuard routes at runtime. */
export function patientBalancePath(patientId: string, balanceId: string): string {
  return `/patient/${patientId}/invoices/${balanceId}`
}

// Distinct + first-seen order. Centralised so CPT-extraction sites can't drift on dedupe semantics.
export function distinctNonEmptyStrings(values: Iterable<string | null | undefined>): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const v of values) {
    if (typeof v !== 'string' || v.length === 0 || seen.has(v)) {
      continue
    }
    seen.add(v)
    out.push(v)
  }
  return out
}

/**
 * Statement AMOUNT chain: `negotiatedAmount → totalCharge → unitCharge × quantity`. Mirrors
 * `resolveChargeAmount` in `apps/api/src/utils/estimate/estimate-template.utils.ts`, which the
 * printed statement uses; `apps/api` defines no path aliases, so the two cannot be shared.
 * Returns `null` rather than a non-finite number so the caller can render "unavailable".
 */
export function resolveChargeAmount(charge: {
  negotiatedAmount?: number
  totalCharge?: number
  unitCharge?: number
  quantity?: number
}): number | null {
  const amount = charge.negotiatedAmount
    ?? charge.totalCharge
    ?? (charge.unitCharge == null ? null : charge.unitCharge * (charge.quantity ?? 1))

  return amount != null && Number.isFinite(amount) ? amount : null
}

/**
 * Mirrors the `?? 1` default `resolveChargeAmount` applies, so a row cannot show 0 units
 * beside a price that was calculated for one. Non-finite stays 0, where the price is `null`.
 */
export function resolveChargeQuantity(quantity: number | undefined): number {
  const value = quantity ?? 1
  return Number.isFinite(value) ? value : 0
}

/** Distinct CPT codes across all sub-items, preserving first-seen order. */
export function balanceCptCodes(balance: EstimateBalance | null | undefined): string[] {
  if (!balance) {
    return []
  }
  return distinctNonEmptyStrings(
    (balance.subItems ?? []).flatMap(s => s.cptCodes ?? []),
  )
}

// null status = no balance row yet → treat as DRAFT so Calculate/Save/Finalize stay visible
// until the first save creates one. Centralised so summary panel + actions menu can't drift.
export function isDraftWorkflow(status: EstimateBalanceWorkflowStatus | null | undefined): boolean {
  return status == null || status === EstimateBalanceWorkflowStatus.DRAFT
}

export function isConfirmedWorkflow(status: EstimateBalanceWorkflowStatus | null | undefined): boolean {
  return status === EstimateBalanceWorkflowStatus.CONFIRMED
}

export function balancesOutstandingTotal(balances: EstimateBalance[] | null | undefined): number {
  if (!balances) {
    return 0
  }
  return balances.reduce((sum, balance) => sum + balanceOutstanding(balance), 0)
}

/**
 * Visibility predicate for the post-finalization panel cluster (collect-payment, plan,
 * transactions). True on CONFIRMED *or* when payment/refund history exists — keeps that
 * history rendered after a CONFIRMED → DRAFT revert so applied payments don't appear to vanish.
 */
export function hasFinalizedActivity(balance: EstimateBalance | null | undefined): boolean {
  if (!balance) {
    return false
  }
  if (isConfirmedWorkflow(balance.workflowStatus)) {
    return true
  }
  if ((balance.paidAmount ?? 0) > 0) {
    return true
  }
  if ((balance.refundedAmount ?? 0) > 0) {
    return true
  }
  return false
}
